import { Module } from 'vuex';
import { getCannedResponses, getCommunityEventResponseOptions } from '@/community-calendar/CommunityCalendarService';
import {
    CommunityCalendarCannedResponses,
    CommunityEventResponseStatusDisplayOption
} from '@/community-calendar/CommunityCalendarModel';
import { UserRoleEnum } from '@/enums/userRolesEnum';

interface CommunityCalendarState {
    reviewOptions: CommunityEventResponseStatusDisplayOption[];
    cannedResponses: CommunityCalendarCannedResponses;
    token: string;
}

export default {
    namespaced: true,
    state: {
        reviewOptions: [],
        cannedResponses: {
            approvedResponse: '',
            rejectedResponse: ''
        },
        token: ''
    },
    actions: {
        async loadReferenceData({commit, state, getters}): Promise<void> {
            if (!getters.isCommunityEventAdmin) {
                return;
            }
            // Loads all reference data asynchronously.
            // Loads all at the same time so it is O(1) rather than O(n).
            // This makes a big difference for the initial load when it has to call the server for each reference type.
            // The normal case is either all will be loaded or none will but this isn't a guarantee
            const promises: Promise<any>[] = [];
            // Push the promise for each type that hasn't yet been loaded with a unique (arbitrary) type
            if (state.reviewOptions.length === 0) {
                promises.push(getCommunityEventResponseOptions(state.token).then(options => ({ id: 1, results: options })));
            }
            if ((!state.cannedResponses.approvedResponse || !state.cannedResponses.rejectedResponse)) {
                promises.push(getCannedResponses(state.token).then(responses => ({ id: 2, results: responses })));
            }
            // When all are complete set the data for each type that returned data.
            (await Promise.all(promises)).forEach(result => {
                switch (result.id) {
                    case 1:
                        commit('setReviewOptions', result.results);
                        break;
                    case 2:
                        commit('setCannedResponses', result.results);
                        break;
                }
            });
        }
    },
    mutations: {
        setReviewOptions(state, payload: CommunityEventResponseStatusDisplayOption[]) {
            state.reviewOptions = payload;
        },
        setCannedResponses(state, payload: CommunityCalendarCannedResponses) {
            state.cannedResponses = payload;
        },
        setToken(state, token: string) {
            state.token = token;
        }
    },
    getters: {
        // If the token is present the user got here from an email.
        // If they're trying to trick us, the server double-checks the token is valid.
        isCommunityEventAdmin(state, getters, rootState, rootGetters): boolean {
            return rootGetters['user/hasRole']([ UserRoleEnum.ADMIN, UserRoleEnum.WEATHER ]) || state.token;
        }
    }
} as Module<CommunityCalendarState, any>;
